import React, { Fragment, useMemo } from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { Popover, RegisterPopperMethods } from 'components';
import IconSelect from 'static/images/categories/icon-site-selection-small.svg';
import IconPotential from 'static/images/categories/icon-invest-potential-small.svg';
import IconProspects from 'static/images/categories/icon-prospects-small.svg';
import IconInfo from 'static/images/categories/icon-general-info-small.svg';
import IconProjects from 'static/images/categories/icon-projects-small.svg';
import IconCross from 'static/images/badge-cross.svg';
import IconLock from 'static/images/icon-lock.svg';
import { getOperationData, getRoots, getCachedPubContainers } from 'selectors';
import { actions as containersActions } from 'services/containers/containers-service';
import { IState } from 'services/root-reducer';
import { useTranslation } from 'react-i18next';
import { isMobile, isTablet } from 'react-device-detect';
import { categoriesConfig, isCategoryAvailable } from './categories-config';

type CategoryContainerCode = 'select' | 'potential' | 'prospects' | 'info';

const Icons = {
    'select': IconSelect,
    'potential': IconPotential,
    'prospects': IconProspects,
    'info': IconInfo,
    'folder5': IconProjects,
    'folder6': IconPotential
};

export interface ICategorySelectorLiteComponent {
    currentCategory: string;
    className?: string;
    onCategoryClick: (category: string) => void;
    /** Сделано для мобильной версии. На мобильниках подсказки показываться не должны */
    disableHints?: boolean;
}

const getCatsActiveContainers = (state: IState, roots) => {
    const containers = getOperationData(state, 'CONTAINERS');
    let res = {};

    roots.forEach((root) => {
        res[root.get('code')] = 0;
    });

    // let rootsWithList = Object.keys(categoriesConfig).reduce((acc, key) => (
    //     [ ...acc, ...categoriesConfig[key].containers ]
    // ), []);
    // if (rootsWithList.indexOf('gen_plans') !== -1) {
    //     rootsWithList.splice(rootsWithList.indexOf('gen_plans'), 1);
    // }

    // console.log(roots, rootsWithList);

    // res = containers.reduce((acc, item) => {
    //     const root = roots.find(r => r.get('id') === item.parent_id);
    //     if (root && rootsWithList.includes(item.code) && item.selected) {
    //         acc[root.get('code')] += 1;
    //     }
    //     return acc;
    // }, res);

    res = roots.reduce((acc, root) => {
        categoriesConfig[root.get('code')]?.containers.forEach(code => {
            const cont = containers.find(c => c.code === code);
            if (cont && cont.selected) {
                acc[root.get('code')] += 1;
            }
        });
        return acc;
    }, res);

    return res;
};


const CategorySelectorLiteComponent: React.FC<ICategorySelectorLiteComponent> = (props): JSX.Element => {
    const {
        currentCategory,
        disableHints = false
    } = props;
    const roots = useSelector(getRoots);
    const containers = useSelector(getCachedPubContainers);
    const catsActiveContainers = useSelector((state: IState) => getCatsActiveContainers(state, roots));
    const [ referenceElement, setReferenceElement ] = React.useState<HTMLDivElement>(null);
    const [ showPopover, setShowPopover ] = React.useState<boolean>(false);
    const [ popoverText, setPopoverText ] = React.useState<string>('');
    const { t } = useTranslation();

    const CategoryLabels = {
        'select': t('Площадки'),
        'potential': t('Инфраструктура'),
        'prospects': t('Потенциал'),
        'info': t('Регион'),
        'folder5': t('Проекты'),
        'folder6': t('Инфраструктура (ЛК)')
    };

    let popperMethods: RegisterPopperMethods = {
        forceUpdate: null,
        update: null
    };

    const commonRoots = useMemo(
        () => roots.filter(r => isCategoryAvailable(r.get('code')) && !categoriesConfig[r.get('code')]?.private),
        [ roots ]
    );
    const privateRoots = useMemo(
        () => roots.filter(r => isCategoryAvailable(r.get('code')) && categoriesConfig[r.get('code')]?.private),
        [ roots ]
    );

    const handleBadgeClick = (evt: React.MouseEvent, category: string) => {
        if ((isMobile || isTablet) && evt.nativeEvent.offsetY > 10) {
            return;
        }
        evt.stopPropagation();
        // const root = roots.find(r => r.get('code') === category);
        //
        // let rootsWithList = Object.keys(categoriesConfig).reduce((acc, key) => (
        //     [ ...acc, ...categoriesConfig[key].containers ]
        // ), []);
        // if (rootsWithList.indexOf('gen_plans') !== -1) {
        //     rootsWithList.splice(rootsWithList.indexOf('gen_plans'), 1);
        // }
        //
        // const conts = containers.filter(c => (
        //     c.get('parent_id') === root.get('id') && rootsWithList.includes(c.get('code'))
        // ));

        const catContainer = categoriesConfig[category].containers;
        const conts = containers.filter(c => (
            catContainer.includes(c.get('code')) || catContainer.includes(c.get('id'))
        ));

        conts.forEach(cont => containersActions.setContainerSelected({
            id_or_code: cont.get('code'),
            selected: false
        }));
    };

    const renderItem = (category: CategoryContainerCode, lock: boolean = false) => {
        const Icon = Icons[category];
        const cont = roots.find(r => r.get('code') === category);

        return (
            <Fragment key={category}>
                <div
                    ref={setReferenceElement}
                    className="iris-category-selector-lite__item-wrapper"
                    onClick={() => props.onCategoryClick(category)}
                    onMouseEnter={(e) => {
                        setPopoverText(cont.get('name'));
                        setReferenceElement(e.currentTarget as HTMLDivElement);
                        setShowPopover(true);
                    }}
                    onMouseLeave={() => setShowPopover(false)}
                >
                    <div
                        className={clsx('iris-category-selector-lite__item', {
                            'active': currentCategory === category
                        })}
                    >
                        {lock && (
                            <div
                                className={clsx('iris-category-selector-lite__item-badge-lock')}
                            >
                                <IconLock />
                            </div>
                        )}
                        <Icon />
                        {catsActiveContainers[category] > 0 && (
                            <div
                                className={clsx(
                                    'iris-category-selector-lite__item-badge',
                                    isMobile && 'hover-off'
                                )}
                                onClick={e => handleBadgeClick(e, category)}
                                onMouseEnter={() => {
                                    setPopoverText(t('Скрыть объекты'));
                                    setShowPopover(true);
                                    popperMethods?.update?.();
                                }}
                                onMouseLeave={() => {
                                    setShowPopover(false);
                                }}
                            >
                                <div className="active-containers-count">
                                    {catsActiveContainers[category]}
                                </div>
                                <IconCross />
                            </div>
                        )}
                    </div>
                    <div className="iris-category-selector-lite__item-label">
                        {CategoryLabels[category]}
                    </div>
                </div>
            </Fragment>
        );
    };

    return (
        <div className={clsx('iris-category-selector-lite', props.className)}>
            <div className="iris-category-selector-lite__section">
                { commonRoots.map(root => renderItem(root.get('code'))) }
            </div>
            {privateRoots.length > 0 && (
                <div className="iris-category-selector-lite__section">
                    {privateRoots.map(root => renderItem(root.get('code'), true))}
                </div>
            )}
            {
                !disableHints && (
                    <Popover
                        className='iris-category-selector-lite__item-popover'
                        open={showPopover}
                        hideTimeout={300}
                        showTimeout={300}
                        targetElement={referenceElement}
                        offset={[ 0, 16 ]}
                        placement="bottom"
                        registerPopperMethods={(methods) => {
                            popperMethods = methods;
                        }}
                        white
                    >
                        <div>{ popoverText }</div>
                    </Popover>
                )
            }
        </div>
    );
};

export const CategorySelectorLite = CategorySelectorLiteComponent;
